/**
 * Zugriff auf die eigene API.
 *
 * `credentials: 'include'` steht hier von Anfang an: die Anmeldung in E1 läuft
 * über ein Cookie, und ein nachträglich vergessenes `include` äußert sich als
 * „ständig ausgeloggt" — ein Fehler, den man lange sucht.
 */

import { CLIENT_HEADER, CLIENT_PROTOCOL } from '@kraftwerk/shared';

// `?.` ist kein Schmuck: außerhalb von Vite (Node-Skripte, Werkzeuge) gibt es
// `import.meta.env` nicht, und ohne die Absicherung kippt schon der Import um.
export const API_URL: string = import.meta.env?.VITE_API_URL ?? 'http://localhost:8787';

/** Jede Anfrage nennt die Fassung ihres Abgleichs (`clientVersion.ts`). */
const CLIENT_HEADERS = { [CLIENT_HEADER]: String(CLIENT_PROTOCOL) };

export interface HealthReport {
  status: 'ok' | 'degraded';
  version: string;
  checks: { database: 'ok' | 'error' };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Maschinenlesbarer Grund aus dem Antwortkörper (`reason`), falls der Server einen nennt. */
    readonly reason: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...CLIENT_HEADERS, ...init.headers },
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    let reason: string | null = null;
    try {
      const body = (await response.json()) as { error?: string; reason?: string };
      if (body.error) message = body.error;
      if (typeof body.reason === 'string') reason = body.reason;
    } catch {
      // Antwort ohne verwertbaren Körper — die Statuszeile muss reichen.
    }
    throw new ApiError(message, response.status, reason);
  }

  // Ohne Körper (204, etwa beim Löschen) gibt es nichts zu lesen.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function fetchHealth(): Promise<HealthReport> {
  return apiFetch<HealthReport>('/health');
}
